import { z } from "zod";
import { SumexXmlVersion } from "./sumex-xml-version";

/**
 * Where the user has to go to fix a field. Only "patient" and "job" live on the
 * job page and can therefore be focused; the rest need navigation instead.
 */
export type SumexFieldLocation =
	| "patient"
	| "job"
	| "position"
	| "client"
	| "organization"
	| "region"
	| "unknown";

interface SumexFieldEntry {
	label: string;
	location: SumexFieldLocation;
	/** Job page field id, required for "patient" and "job" so the field can be focused. */
	jobField?: string;
}

export interface PreflightProblem {
	wireField: string;
	label: string;
	location: SumexFieldLocation;
	jobField?: string;
	reason: string;
	/** 1-based, only for "position". */
	positionNumber?: number;
}

const LOCATION_HINT: Record<SumexFieldLocation, string> = {
	patient: "in den Patientendaten",
	job: "im Auftrag",
	position: "bei den Positionen",
	client: "in den Kundeneinstellungen",
	organization: "in den Organisationseinstellungen",
	region: "in der Region des Kunden",
	unknown: "in den Auftragsdaten",
};

export const SUMEX_FIELD_REGISTRY: Record<string, SumexFieldEntry> = {
	LABCanton: { label: "Kanton", location: "organization" },
	LABCompany: { label: "Firmenname", location: "organization" },
	LABName: { label: "Name", location: "organization" },
	LABStreet: { label: "Strasse", location: "organization" },
	LABCity: { label: "Ort", location: "organization" },
	LABPLZ: { label: "Postleitzahl", location: "organization" },
	LABPhone: { label: "Telefonnummer", location: "organization" },
	LABEmail: { label: "E-Mail", location: "organization" },
	LABGLN: { label: "GLN", location: "organization" },
	// The MwSt-Nummer belongs to the client's region (Kundeneinstellungen → Rechnungen),
	// not to the lab, and is copied onto the job when the job is created.
	LABVATNumber: { label: "MwSt-Nummer", location: "region" },
	LABIBAN: {
		label: "QR-IBAN des Bankkontos",
		location: "job",
		jobField: "jobs.bank_account_id",
	},

	// A jobField is set only where the job page actually renders that field (see
	// FIELD_REGISTRY). Without one the problem is still named in the message, it just
	// cannot be focused — correct for the job code, which lives in the page header,
	// and for the document date, which belongs to the document rather than the job.
	ATRCode: { label: "Auftragscode", location: "job" },
	ATRNote: {
		label: "Auftragstitel",
		location: "job",
		jobField: "jobs.title",
	},
	ATRVAT: {
		label: "MwSt-Satz",
		location: "job",
		jobField: "jobs.tax_rate",
	},
	ATRDate: { label: "Datum des Dokuments", location: "job" },
	ATRProduction: {
		label: "Fertigungsland",
		location: "job",
		jobField: "jobs.prod_country_choice_or_other",
	},
	ATRDiscount1: { label: "Rabatt 1", location: "job" },
	ATRDiscount2: { label: "Rabatt 2", location: "job" },

	PATCode: {
		label: "Patientencode",
		location: "patient",
		jobField: "patients.code",
	},
	// Never fails validation (optional in the payload schema); listed only so the
	// label exists if that ever changes.
	PATSalutation: {
		label: "Anrede",
		location: "patient",
		jobField: "patients.title",
	},
	PATFirstName: {
		label: "Vorname",
		location: "patient",
		jobField: "patients.first_name",
	},
	PATLastName: {
		label: "Nachname",
		location: "patient",
		jobField: "patients.last_name",
	},
	PATBirthDate: {
		label: "Geburtsdatum",
		location: "patient",
		jobField: "patients.birth_date",
	},
	// Hardcoded to "SUVA" in the payload mapping, so it cannot fail validation.
	PATProvider: { label: "Versicherer", location: "patient" },

	KUNCode: { label: "Kundencode", location: "client" },
	KUNSalutation: { label: "Anrede", location: "client" },
	KUNFirstName: { label: "Vorname", location: "client" },
	KUNLastName: { label: "Name bzw. Firmenname", location: "client" },
	KUNStreet: { label: "Strasse", location: "client" },
	KUNCity: { label: "Ort", location: "client" },
	KUNPLZ: { label: "Postleitzahl", location: "client" },
	KUNPhone: { label: "Telefonnummer", location: "client" },
	KUNEmail: { label: "E-Mail", location: "client" },
};

const POSITION_FIELD_LABELS: Record<string, string> = {
	code: "Code",
	description: "Beschreibung",
	quantity: "Menge",
	Date: "Datum",
	price: "Preis",
	TPW: "Taxpunktwert",
	Art: "Typ",
};

export const problemForWireField = (
	wireField: string,
	reason: string
): PreflightProblem => {
	const entry = SUMEX_FIELD_REGISTRY[wireField];
	return {
		wireField,
		label: entry?.label ?? wireField,
		location: entry?.location ?? "unknown",
		jobField: entry?.jobField,
		reason,
	};
};

export const mapZodIssuesToProblems = (
	issues: z.ZodIssue[]
): PreflightProblem[] => {
	const seen = new Set<string>();
	const problems: PreflightProblem[] = [];

	for (const issue of issues) {
		const [root, second, third] = issue.path;

		if (root === "documentTypes") {
			const field = String(third ?? "");
			const problem: PreflightProblem = {
				wireField: field,
				label: POSITION_FIELD_LABELS[field] ?? field,
				location: "position",
				reason: issue.message,
				positionNumber:
					typeof second === "number" ? second + 1 : undefined,
			};
			const key = `position:${problem.positionNumber}:${field}`;
			if (seen.has(key)) continue;
			seen.add(key);
			problems.push(problem);
			continue;
		}

		const wireField = String(second ?? root ?? "");
		if (seen.has(wireField)) continue;
		seen.add(wireField);

		problems.push(problemForWireField(wireField, issue.message));
	}

	return problems;
};

const describeProblem = (problem: PreflightProblem): string => {
	if (problem.location === "position") {
		const position = problem.positionNumber
			? `Position ${problem.positionNumber}`
			: "Eine Position";
		return `${position}: ${problem.reason}`;
	}

	return `${problem.label} (${LOCATION_HINT[problem.location]}): ${
		problem.reason
	}`;
};

export const buildPreflightMessage = (
	problems: PreflightProblem[],
	version: SumexXmlVersion
): string => {
	const lines = problems.map(describeProblem);

	const needsBirthDateNote =
		version === SumexXmlVersion.V500 &&
		problems.some((problem) => problem.wireField === "PATBirthDate");

	if (needsBirthDateNote) {
		lines.push("Für XML 5.00 ist das Geburtsdatum zwingend erforderlich.");
	}

	return lines.join("\n");
};

/**
 * Where to send the user when the missing field lives on another screen. Returns
 * undefined when the problems are all fixable on the job page they are already on.
 */
export const settingsTargetFor = (
	problems: PreflightProblem[],
	clientId: string | null | undefined
): { label: string; path: string } | undefined => {
	const offSite = problems.filter(
		(problem) =>
			problem.location === "client" ||
			problem.location === "organization" ||
			problem.location === "region"
	);
	if (offSite.length === 0) return undefined;

	const locations = new Set(offSite.map((problem) => problem.location));
	if (locations.size > 1) return undefined;

	if (locations.has("organization")) {
		return {
			label: "Organisationseinstellungen öffnen",
			path: "/organisation/administration/firmenangaben",
		};
	}

	if (!clientId) return undefined;

	if (locations.has("region")) {
		return {
			label: "Region des Kunden öffnen",
			path: `/client/${clientId}/einstellungen/rechnungen`,
		};
	}

	return {
		label: "Kundeneinstellungen öffnen",
		path: `/client/${clientId}/einstellungen/dokumente`,
	};
};
